import React, { useEffect, useState } from 'react'
import { Contact } from '../../shared/components'
import {
  MAX_CALLS,
  MONTHS_PER_CALL,
  startOrEndDateForEvent,
  getPlanAheadFromEvent
} from '../helpers/calendar'
import { DateTime, Duration, Interval } from 'luxon'
import DateFormatter from '../../shared/helpers/date-format'
import ListViewDisplay from './list-view-display'
import styles from '../components/date-picker-styles.module.css'
import {
  BackButton,
  NextButton
} from '../../calendar/components/header/buttons'
import { map } from 'lodash-es'
import storage from '../../services/storage'

const ListViewFromEvent = ({
  onDayClick,
  loadingCalendar,
  setLoading,
  calendarStartDate,
  fromEvent,
  minimumAvailability,
  TEST_JS_DATE,
  storageDateTime,
  setError,
  selectDay,
  hours,
  selectedDate,
  priceTableInfo,
  setPriceTableInfo,
  isMobiDisplayed,
  showModal,
  remainingTicketsLoading
}) => {
  //clickedElement is used to set styling for selected elements and to hide unselected open alerts
  const [clickedElement, setClickedElement] = useState()
  const [planningInfoFromEvent, setPlanningInfoFromEvent] = useState([])
  const [eventStartDate, setEventStartDate] = useState(
    calendarStartDate || DateTime.fromJSDate(TEST_JS_DATE)
  )
  const [eventEndDate, setEventEndDate] = useState(
    startOrEndDateForEvent(
      calendarStartDate || DateTime.fromJSDate(TEST_JS_DATE),
      false
    )
  )
  const [cantAdvance, setCantAdvance] = useState(false)
  const [useStorageDateTime, setUseStorageDateTime] = useState(storageDateTime)
  // const [numberApiCalls, setNumberApiCalls] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [lastPage, setLastPage] = useState()
  const DATESPERPAGE = 7

  // fromEvent needs minimumAvailability
  const fetchPlanAheadListView = async (
    startDate,
    fromEvent,
    minimumAvailability,
    endDate
  ) => {
    try {
      const { planningInfo, noDatesAvailable } = await getPlanAheadFromEvent(
        startDate,
        fromEvent,
        minimumAvailability,
        endDate
      )

      if (noDatesAvailable && planningInfoFromEvent.length === 0) {
        setCantAdvance(noDatesAvailable)
      } else {
        if (!noDatesAvailable) {
          setEventEndDate(endDate)
        }
        // add the newest set of planningInfo to the current array
        setPlanningInfoFromEvent(prevState => [
          ...new Set([...prevState, planningInfo].flat(1))
        ])
        // this is so when the last date before navigating forward is less than a full page, it stays on the current page for continuity
        let firstIndexOnPage = currentPage * DATESPERPAGE
        if (
          planningInfoFromEvent.length - firstIndexOnPage < 7 &&
          !noDatesAvailable
        ) {
          setCurrentPage(currentPage)
        }
      }

      setLastPage(
        Math.ceil(
          (planningInfoFromEvent.length + planningInfo.length) / DATESPERPAGE
        ) - 1
      )
      setLoading(false)
    } catch {
      selectDay(null)
      storage.clear()
      setError(true)
    }
  }

  const fetchStoredDatePlanAheadListView = async (
    startDate,
    fromEvent,
    minimumAvailability,
    storageEndDate
  ) => {
    try {
      let number_to_call_for_stored_date = Math.ceil(
        Interval.fromDateTimes(startDate, storageEndDate).length('months') /
          MONTHS_PER_CALL
      )
      let number_of_month_calls = 0
      let storedPlanningInfo = []

      while (number_of_month_calls < number_to_call_for_stored_date) {
        let newStartDate = startDate.plus(
          Duration.fromObject({
            months: MONTHS_PER_CALL * number_of_month_calls
          })
        )
        let newEndDate = startDate.plus(
          Duration.fromObject({
            months: MONTHS_PER_CALL * (number_of_month_calls + 1)
          })
        )
        let { planningInfo } = await getPlanAheadFromEvent(
          newStartDate,
          fromEvent,
          minimumAvailability,
          newEndDate
        )
        storedPlanningInfo = [storedPlanningInfo, planningInfo].flat(1)
        number_of_month_calls += 1
      }

      if (storedPlanningInfo.length === 0) {
        setCantAdvance(true)
      } else {
        // add the newest set of planningInfo to the current array
        setPlanningInfoFromEvent(prevState =>
          [...prevState, storedPlanningInfo].flat(1)
        )
      }

      // calculate currentPage to return to based on the final array of event info
      storedPlanningInfo = [
        ...new Set([storedPlanningInfo, planningInfoFromEvent].flat(1))
      ]
      let indexOfStorageDateTime = map(storedPlanningInfo, 'date').indexOf(
        DateFormatter.dashed(useStorageDateTime)
      )
      // this takes the user to whatever page their selected date is on when then click back from the selected date page
      setCurrentPage(Math.round(indexOfStorageDateTime / DATESPERPAGE))

      setLastPage(Math.ceil(storedPlanningInfo.length / DATESPERPAGE) - 1)
      setLoading(false)
    } catch {
      selectDay(null)
      storage.clear()
      setError(true)
    }
  }

  const fetchInitialPlanAheadListView = async (
    eventStartDate,
    fromEvent,
    minimumAvailability
  ) => {
    try {
      let number_of_month_calls = 0
      while (number_of_month_calls < MAX_CALLS) {
        let newStartDate = eventStartDate.plus(
          Duration.fromObject({
            months: MONTHS_PER_CALL * number_of_month_calls
          })
        )
        let newEndDate = eventStartDate.endOf('month').plus(
          Duration.fromObject({
            months: MONTHS_PER_CALL * (number_of_month_calls + 1)
          })
        )

        let { planningInfo, noDatesAvailable } = await getPlanAheadFromEvent(
          newStartDate,
          fromEvent,
          minimumAvailability,
          newEndDate
        )

        if (!noDatesAvailable || number_of_month_calls === MAX_CALLS - 1) {
          setEventStartDate(newStartDate)
          setEventEndDate(newEndDate)

          if (noDatesAvailable && planningInfoFromEvent.length === 0) {
            setCantAdvance(noDatesAvailable)
          } else {
            // add the newest set of planningInfo to the current array
            setPlanningInfoFromEvent(prevState => [
              ...new Set([...prevState, planningInfo].flat(1))
            ])
          }

          setLastPage(
            Math.ceil(
              (planningInfoFromEvent.length + planningInfo.length) /
                DATESPERPAGE
            ) - 1
          )
          setLoading(false)

          return
        } else {
          number_of_month_calls += 1
        }
      }
    } catch {
      selectDay(null)
      storage.clear()
      setError(true)
    }
  }

  useEffect(() => {
    setLoading(true)

    if (useStorageDateTime) {
      let newEndDate = useStorageDateTime.endOf('month')
      setEventEndDate(newEndDate)
      fetchStoredDatePlanAheadListView(
        eventStartDate,
        fromEvent,
        minimumAvailability,
        newEndDate
      )
    } else {
      // SHEDDWEB-1112: don't need eventEndDate initially since we'll look in 3 month chunks for up to a year
      fetchInitialPlanAheadListView(
        eventStartDate,
        fromEvent,
        minimumAvailability
      )
    }
  }, [])

  const navigate = (isForward = false) => {
    setUseStorageDateTime(null)
    if (isForward) {
      setCurrentPage(currentPage + 1)
      if (currentPage === lastPage) {
        // SHEDDWEB-1112: when at the end of the pages, the next call will look at the next 3 months
        setLoading(true)
        let newStartDate = eventEndDate.plus(
          Duration.fromObject({
            days: 1
          })
        )
        let newEndDate = eventEndDate.endOf('month').plus(
          Duration.fromObject({
            months: MONTHS_PER_CALL
          })
        )
        fetchPlanAheadListView(
          newStartDate,
          fromEvent,
          minimumAvailability,
          newEndDate
        )
      }
    } else {
      setCurrentPage(currentPage - 1)
    }
  }

  const isButtonDisabled = (isBackward = false) => {
    if (isBackward) return currentPage === 0
    // removed for case when call returns 1- 7 dates at end of range
    // there could be more dates on next call
    // if (planningInfoFromEvent.length <= DATESPERPAGE) return true
    if (cantAdvance) return currentPage === lastPage + 1
    if (!isBackward && pagedPlanningInfo.length === 0) return true
    return false
  }

  const firstIndexOnPage = currentPage * DATESPERPAGE
  const pagedPlanningInfo = planningInfoFromEvent.slice(
    firstIndexOnPage,
    currentPage * DATESPERPAGE + DATESPERPAGE
  )
  const mapPlanningInfo = () => {
    return pagedPlanningInfo.map(oneDay => {
      return (
        <ListViewDisplay
          dateTime={DateTime.fromISO(oneDay.date, { zone: 'America/Chicago' })}
          unavailableDay={false}
          onDayClick={onDayClick}
          oneDay={oneDay}
          key={Math.random(900)}
          clickedElement={clickedElement}
          setClickedElement={setClickedElement}
          fromEvent={fromEvent}
          hours={hours}
          selectedDate={selectedDate}
          priceTableInfo={priceTableInfo}
          setPriceTableInfo={setPriceTableInfo}
          isMobiDisplayed={isMobiDisplayed}
          showModal={showModal}
          remainingTicketsLoading={remainingTicketsLoading}
        />
      )
    })
  }

  if (cantAdvance) {
    return (
      <Contact
        preText="There are no available dates for a party of your size. Please adjust your party size and try again."
        postText=""
        phone=""
        email=""
      />
    )
  } else {
    return (
      !loadingCalendar && (
        <React.Fragment>
          <h1
            className="text-24 text-depths"
            tabIndex={0}
          >
            Available dates between {DateFormatter.slashed(eventStartDate)} and{' '}
            {DateFormatter.slashed(eventEndDate)}
          </h1>
          <div className="mb-32">
            <BackButton
              onClick={() => navigate()}
              isSingleDay={false}
              disabled={isButtonDisabled(true)}
            />
            <p
              className={
                isButtonDisabled()
                  ? 'inline-flex text-black-22 xs-paragraph mx-12'
                  : 'inline-flex text-depths xs-paragraph mx-12'
              }
            >
              Next {DATESPERPAGE} dates
            </p>
            <NextButton
              onClick={() => navigate(true)}
              isSingleDay={false}
              disabled={isButtonDisabled()}
            />
          </div>
          {pagedPlanningInfo.length === 0 && (
            <div
              tabIndex={0}
              data-testid="no-more-dates"
              className={
                styles['mobi-event-unavailable'] +
                ' border ' +
                styles['mobi-rectangle']
              }
            >
              You have passed the date range when tickets are available.
            </div>
          )}

          {mapPlanningInfo()}
        </React.Fragment>
      )
    )
  }
}

export default ListViewFromEvent
